/**
 * Global error handling utilities for unhandled promise rejections and global JavaScript errors.
 * Coordinates with existing Meta Pixel error handlers and reports errors to Sentry —
 * captureException buffers until the deferred SDK init completes, so errors thrown
 * before first interaction (boot crashes) are reported too instead of dropped.
 * Update when modifying global error handling patterns or adding new error filtering logic.
 */

import { captureException } from '@/services/sentryService';

let isSetup = false;

// Sentry's serialization, transport, or a cross-realm Error can produce a
// fresh unhandled rejection while we're already handling one. Without a cap,
// the handler re-enters indefinitely and overflows the stack on iOS WKWebView
// (Sentry issues 118885712 / 118885748). The window resets after a quiet
// period so legitimate bursts of unrelated errors aren't silently dropped.
const RECURSION_GUARD_WINDOW_MS = 5000;
const RECURSION_GUARD_MAX = 10;
let rejectionWindowStart = 0;
let rejectionCount = 0;
let errorWindowStart = 0;
let errorCount = 0;

const shouldThrottleRejection = (): boolean => {
  const now = Date.now();
  if (now - rejectionWindowStart > RECURSION_GUARD_WINDOW_MS) {
    rejectionWindowStart = now;
    rejectionCount = 0;
  }
  rejectionCount += 1;
  return rejectionCount > RECURSION_GUARD_MAX;
};

const shouldThrottleError = (): boolean => {
  const now = Date.now();
  if (now - errorWindowStart > RECURSION_GUARD_WINDOW_MS) {
    errorWindowStart = now;
    errorCount = 0;
  }
  errorCount += 1;
  return errorCount > RECURSION_GUARD_MAX;
};

// Cross-realm Errors (iframe, web worker, WKWebView injection on iOS Chrome)
// fail `instanceof Error` because their prototype chain points at a different
// realm's `Error.prototype`. Duck-typing preserves the original stack instead
// of wrapping into a new Error whose message is "Error: <original message>".
const toErrorObject = (value: unknown): Error => {
  if (value instanceof Error) {
    return value;
  }
  if (
    !!value &&
    typeof value === 'object' &&
    typeof (value as { message?: unknown }).message === 'string' &&
    typeof (value as { stack?: unknown }).stack === 'string'
  ) {
    return value as Error;
  }
  return new Error(typeof value === 'string' ? value : String(value));
};

interface Auth0User {
  sub: string;
  email: string;
  name: string;
}

interface UserInfo {
  id: string;
  email: string;
  name: string;
}

declare global {
  interface Window {
    __AUTH0_USER__?: Auth0User;
    _metaPixelErrorHandlerAdded?: boolean;
  }
}

/**
 * Checks if an error is from Meta Pixel (Facebook Pixel) and should be filtered out.
 * Used to prevent reporting non-critical third-party script errors.
 */
const isMetaPixelError = (
  error: Error | string | unknown,
  source?: string
): boolean => {
  // Check source URL
  if (source && typeof source === 'string') {
    if (source.includes('connect.facebook.net')) {
      return true;
    }
  }

  // Check error message
  const message =
    typeof error === 'string'
      ? error
      : (error as Error)?.message || String(error ?? '');
  if (typeof message === 'string') {
    const lowerMessage = message.toLowerCase();
    if (
      lowerMessage.includes('facebook') ||
      lowerMessage.includes('meta pixel') ||
      lowerMessage.includes('fbq')
    ) {
      return true;
    }
  }

  // Check stack trace
  const stack = (error as Error)?.stack;
  if (stack && typeof stack === 'string') {
    if (stack.toLowerCase().includes('connect.facebook.net')) {
      return true;
    }
  }

  return false;
};

/**
 * Gets current route information from window location.
 */
const getCurrentRoute = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }
  return window.location?.pathname || 'unknown';
};

/**
 * Gets user information if available from global context.
 */
const getUserInfo = (): UserInfo | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const auth0User = window.__AUTH0_USER__;
    if (auth0User) {
      return {
        id: auth0User.sub,
        email: auth0User.email,
        name: auth0User.name,
      };
    }
  } catch {
    // Silently fail - user context is optional
  }

  return null;
};

/**
 * Handles unhandled promise rejections.
 */
const handleUnhandledRejection = (event: PromiseRejectionEvent): boolean => {
  const error = event.reason;
  const route = getCurrentRoute();

  if (!import.meta.env.PROD) {
    console.error('Unhandled promise rejection:', error);
    console.error('Route:', route);
  }

  if (!shouldThrottleRejection()) {
    try {
      const userInfo = getUserInfo();
      captureException(toErrorObject(error), {
        level: 'error',
        tags: {
          errorType: 'unhandled_promise_rejection',
          route,
        },
        extra: {
          route,
          userInfo: userInfo || undefined,
          timestamp: new Date().toISOString(),
        },
      });
    } catch (sentryError) {
      console.warn(
        'Failed to report unhandled rejection to Sentry:',
        sentryError
      );
    }
  }

  return false;
};

/**
 * Handles global JavaScript errors via window.onerror.
 */
const handleGlobalError = (
  message: string | Event,
  source: string | undefined,
  lineno: number | undefined,
  colno: number | undefined,
  error: Error | undefined
): boolean => {
  const route = getCurrentRoute();

  if (!import.meta.env.PROD) {
    console.error('Global error:', message, source, lineno, colno, error);
    console.error('Route:', route);
  }

  if (!shouldThrottleError()) {
    try {
      const errorToReport =
        error ||
        new Error(typeof message === 'string' ? message : 'Unknown error');
      const userInfo = getUserInfo();
      captureException(errorToReport, {
        level: 'error',
        tags: {
          errorType: 'global_error',
          route,
        },
        extra: {
          message: typeof message === 'string' ? message : 'Unknown error',
          source,
          lineno,
          colno,
          route,
          userInfo: userInfo || undefined,
          timestamp: new Date().toISOString(),
        },
      });
    } catch (sentryError) {
      console.warn('Failed to report global error to Sentry:', sentryError);
    }
  }

  return false;
};

/**
 * Sets up global error handlers for unhandled promise rejections and global JavaScript errors.
 * Coordinates with existing Meta Pixel error handlers and reports errors to Sentry when available.
 * This function is idempotent and safe to call multiple times.
 */
export const setupGlobalErrorHandlers = (): void => {
  if (isSetup) {
    return;
  }

  try {
    const existingUnhandledRejection = window.onunhandledrejection;
    const existingErrorHandler = window.onerror;
    const hasMetaPixelHandler = window._metaPixelErrorHandlerAdded;

    window.onunhandledrejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const isMetaPixel = isMetaPixelError(
        error,
        error?.stack || error?.message
      );

      if (hasMetaPixelHandler && isMetaPixel && existingUnhandledRejection) {
        try {
          existingUnhandledRejection.call(window, event);
        } catch (chainError) {
          console.warn(
            'Existing unhandled rejection handler threw:',
            chainError
          );
        }
        return;
      }

      if (isMetaPixel) {
        event.preventDefault();
        return;
      }

      handleUnhandledRejection(event);

      if (existingUnhandledRejection) {
        try {
          existingUnhandledRejection.call(window, event);
        } catch (chainError) {
          console.warn(
            'Existing unhandled rejection handler threw:',
            chainError
          );
        }
      }
    };

    window.onerror = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error
    ): boolean => {
      const isMetaPixel = isMetaPixelError(error || message, source);

      if (hasMetaPixelHandler && isMetaPixel && existingErrorHandler) {
        try {
          return (
            existingErrorHandler(message, source, lineno, colno, error) ?? false
          );
        } catch (chainError) {
          console.warn('Existing error handler threw:', chainError);
          return false;
        }
      }

      if (isMetaPixel) {
        return true;
      }

      handleGlobalError(message, source, lineno, colno, error);

      if (existingErrorHandler) {
        try {
          return (
            existingErrorHandler(message, source, lineno, colno, error) ?? false
          );
        } catch (chainError) {
          console.warn('Existing error handler threw:', chainError);
          return false;
        }
      }

      return false;
    };

    isSetup = true;
  } catch (error) {
    console.warn('Failed to set up global error handlers:', error);
  }
};
